import { useEffect, useMemo, useState, type JSX } from 'react';
import { ClipboardList, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { FilterField } from '@/components/ui/filter-field';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import { channelLabel } from '@/components/ui/tag';
import { AccountFormDialog } from '@/components/outreach-accounts/AccountFormDialog';
import { AccountsTable } from '@/components/outreach-accounts/AccountsTable';
import { BulkAddDialog } from '@/components/outreach-accounts/BulkAddDialog';
import { VaultNotConfigured } from '@/components/outreach-accounts/VaultNotConfigured';
import { isVaultNotConfigured, useOutreachAccounts } from '@/hooks/useOutreachAccounts';
import { useDebounce } from '@/hooks/useDebounce';
import { useUrlState } from '@/hooks/useUrlState';
import { ACCOUNT_STATUS_LABEL } from '@/lib/outreachAccounts';
import { cn } from '@/lib/utils';
import {
  INVITE_CHANNELS,
  OUTREACH_ACCOUNT_STATUSES,
  type InviteChannel,
  type OutreachAccountStatus,
} from '@/api/types';

/**
 * Every outreacher's DM logins in one place (admin only). Filter by
 * outreacher, platform, status or a search across handle, email, notes and
 * outreacher name; the strip above the table counts accounts per outreacher
 * and doubles as the outreacher filter.
 */
export function AdminOutreachAccounts(): JSX.Element {
  const [f, setF] = useUrlState({ outreacher: '', platform: '', status: '', q: '' });
  const [searchInput, setSearchInput] = useState(f.q);
  const search = useDebounce(searchInput, 250);
  useEffect(() => {
    if (search !== f.q) setF({ q: search });
  }, [search, f.q, setF]);

  const accounts = useOutreachAccounts({
    inviterId: f.outreacher || undefined,
    platform: (f.platform as InviteChannel) || '',
    status: (f.status as OutreachAccountStatus) || '',
    search: f.q,
  });
  const [adding, setAdding] = useState(false);
  const [pasting, setPasting] = useState(false);

  const counts = accounts.data?.countsByInviter ?? [];
  const totalAll = counts.reduce((n, c) => n + c.total, 0);
  const outreacherOptions = useMemo(
    () => counts.map((c) => ({ value: c.inviterPublicId, label: c.inviterName, hint: String(c.total) })),
    [counts],
  );
  const notConfigured = isVaultNotConfigured(accounts.error);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Outreach accounts"
        description="The Instagram, TikTok and other logins each outreacher messages creators from. Only admins can see this page; every password reveal is logged."
        actions={
          notConfigured ? undefined : (
            <>
              <Button variant="outline" onClick={() => setPasting(true)}>
                <ClipboardList className="h-4 w-4" /> Paste many
              </Button>
              <Button onClick={() => setAdding(true)}>
                <Plus className="h-4 w-4" /> Add account
              </Button>
            </>
          )
        }
      />

      {notConfigured ? (
        <VaultNotConfigured />
      ) : (
        <>
          {counts.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5" aria-label="Accounts per outreacher">
              <CountChip active={!f.outreacher} onClick={() => setF({ outreacher: '' })} label="Everyone" count={totalAll} />
              {counts.map((c) => (
                <CountChip
                  key={c.inviterPublicId}
                  active={f.outreacher === c.inviterPublicId}
                  onClick={() => setF({ outreacher: f.outreacher === c.inviterPublicId ? '' : c.inviterPublicId })}
                  label={c.inviterName}
                  count={c.total}
                  title={`${c.active} active · ${Object.entries(c.byPlatform)
                    .map(([p, n]) => `${channelLabel(p)} ${n}`)
                    .join(', ')}`}
                  muted={!c.inviterIsActive}
                />
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-end gap-3">
            <FilterField label="Search">
              <Input
                className="w-full md:w-64"
                type="search"
                aria-label="Search accounts"
                placeholder="Handle, email, notes or outreacher"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </FilterField>
            <FilterField label="Outreacher">
              <Combobox
                className="w-full md:w-48"
                aria-label="Outreacher"
                value={f.outreacher}
                onChange={(v) => setF({ outreacher: v })}
                options={outreacherOptions}
                allowAll
                allLabel="All outreachers"
              />
            </FilterField>
            <FilterField label="Platform">
              <Combobox
                className="w-full md:w-40"
                aria-label="Platform"
                value={f.platform}
                onChange={(v) => setF({ platform: v })}
                options={INVITE_CHANNELS.map((c) => ({ value: c, label: channelLabel(c) }))}
                allowAll
                allLabel="All platforms"
              />
            </FilterField>
            <FilterField label="Status">
              <Combobox
                className="w-full md:w-36"
                aria-label="Status"
                value={f.status}
                onChange={(v) => setF({ status: v })}
                options={OUTREACH_ACCOUNT_STATUSES.map((s) => ({ value: s, label: ACCOUNT_STATUS_LABEL[s] }))}
                allowAll
                allLabel="Any status"
              />
            </FilterField>
          </div>

          {accounts.error ? (
            <p role="alert" className="text-sm text-destructive">
              Couldn't load accounts: {(accounts.error as Error).message}
            </p>
          ) : (
            <AccountsTable
              accounts={accounts.data?.accounts ?? []}
              loading={accounts.isLoading}
              showOutreacher
              emptyHint={
                f.outreacher || f.platform || f.status || f.q
                  ? 'Nothing matches these filters.'
                  : 'Add the logins your outreachers message creators from, one at a time or pasted as a list.'
              }
            />
          )}
        </>
      )}

      <AccountFormDialog open={adding} onOpenChange={setAdding} />
      <BulkAddDialog open={pasting} onOpenChange={setPasting} />
    </div>
  );
}

function CountChip({
  active,
  onClick,
  label,
  count,
  title,
  muted,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  title?: string;
  muted?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={title}
      className={cn(
        'inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition-colors pointer-coarse:h-9',
        active ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:bg-accent',
        muted && !active && 'text-muted-foreground',
      )}
    >
      {label}
      <span className={cn('font-mono tabular-nums', active ? 'opacity-90' : 'text-muted-foreground')}>{count}</span>
    </button>
  );
}
